import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { customerApi } from "@/services/api/customers";

// Types for dashboard data
export interface DashboardStats {
  savedProperties: number;
  upcomingTours: number;
  messages: number;
  marketAlerts: number;
}

export interface RecentActivity {
  id: string;
  type:
    | "saved_property"
    | "scheduled_tour"
    | "message_received"
    | "price_alert";
  title: string;
  description: string;
  timestamp: string;
  propertyId?: string;
  agentId?: string;
}

export interface MarketAlert {
  id: string;
  type: "price_drop" | "new_listing" | "market_update";
  title: string;
  description: string;
  propertyId?: string;
  timestamp: string;
  isRead: boolean;
}

export interface DashboardData {
  stats: DashboardStats;
  recentActivity: RecentActivity[];
  marketAlerts: MarketAlert[];
  recommendedProperties: string[]; // Property IDs
  topAgents: string[]; // Agent IDs
}

interface CustomerDashboardState {
  dashboardData: DashboardData | null;
  isLoading: boolean;
  error: string | null;
  lastUpdated: string | null;
}

const initialState: CustomerDashboardState = {
  dashboardData: null,
  isLoading: false,
  error: null,
  lastUpdated: null,
};

// Mock data for development
const mockDashboardData: DashboardData = {
  stats: {
    savedProperties: 12,
    upcomingTours: 3,
    messages: 5,
    marketAlerts: 2,
  },
  recentActivity: [
    {
      id: "1",
      type: "saved_property",
      title: "Saved a new property",
      description: "Luxury Downtown Condo - 2 hours ago",
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      propertyId: "prop1",
    },
    {
      id: "2",
      type: "scheduled_tour",
      title: "Scheduled a tour",
      description: "Modern Family Home - Tomorrow at 2:00 PM",
      timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
      propertyId: "prop2",
    },
    {
      id: "3",
      type: "message_received",
      title: "Received message from agent",
      description: "Sarah Johnson - 1 day ago",
      timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
      agentId: "agent1",
    },
  ],
  marketAlerts: [
    {
      id: "1",
      type: "price_drop",
      title: "Price Drop Alert",
      description: "Downtown Condo reduced by $25,000",
      propertyId: "prop3",
      timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
      isRead: false,
    },
    {
      id: "2",
      type: "new_listing",
      title: "New Listing",
      description: "5 new properties in your preferred area",
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      isRead: false,
    },
  ],
  recommendedProperties: ["prop1", "prop2", "prop3", "prop4"],
  topAgents: ["agent1", "agent2", "agent3"],
};

// Async thunks
export const fetchDashboardData = createAsyncThunk(
  "customerDashboard/fetchDashboardData",
  async (_, { rejectWithValue }) => {
    try {
      // Get dashboard data (API doesn't accept parameters currently)
      const dashboardData = await customerApi.getDashboard();
      return dashboardData;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch dashboard data"
      );
    }
  }
);

export const markAlertAsRead = createAsyncThunk(
  "customerDashboard/markAlertAsRead",
  async (alertId: string, { rejectWithValue }) => {
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 500));

      // In a real implementation, this would make an API call to:
      // PUT /api/customer/alerts/{alertId}/read
      return alertId;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to mark alert as read");
    }
  }
);

export const refreshDashboardData = createAsyncThunk(
  "customerDashboard/refreshDashboardData",
  async (_, { rejectWithValue }) => {
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 800));

      // Return updated mock data
      return {
        ...mockDashboardData,
        stats: {
          ...mockDashboardData.stats,
          messages: mockDashboardData.stats.messages + 1,
        },
        lastUpdated: new Date().toISOString(),
      };
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to refresh dashboard data"
      );
    }
  }
);

const customerDashboardSlice = createSlice({
  name: "customerDashboard",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearDashboardData: (state) => {
      state.dashboardData = null;
      state.lastUpdated = null;
    },
    updateStats: (state, action: PayloadAction<Partial<DashboardStats>>) => {
      if (state.dashboardData) {
        state.dashboardData.stats = {
          ...state.dashboardData.stats,
          ...action.payload,
        };
      }
    },
    addRecentActivity: (state, action: PayloadAction<RecentActivity>) => {
      if (state.dashboardData) {
        state.dashboardData.recentActivity.unshift(action.payload);
        // Keep only the last 10 activities
        if (state.dashboardData.recentActivity.length > 10) {
          state.dashboardData.recentActivity =
            state.dashboardData.recentActivity.slice(0, 10);
        }
      }
    },
    removeRecentActivity: (state, action: PayloadAction<string>) => {
      if (state.dashboardData) {
        state.dashboardData.recentActivity =
          state.dashboardData.recentActivity.filter(
            (activity) => activity.id !== action.payload
          );
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch dashboard data
      .addCase(fetchDashboardData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDashboardData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dashboardData = action.payload;
        state.lastUpdated = new Date().toISOString();
        state.error = null;
      })
      .addCase(fetchDashboardData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Mark alert as read
      .addCase(markAlertAsRead.pending, () => {
        // No loading state for this action
      })
      .addCase(markAlertAsRead.fulfilled, (state, action) => {
        if (state.dashboardData) {
          const alert = state.dashboardData.marketAlerts.find(
            (alert) => alert.id === action.payload
          );
          if (alert) {
            alert.isRead = true;
          }
        }
      })
      .addCase(markAlertAsRead.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      // Refresh dashboard data
      .addCase(refreshDashboardData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(refreshDashboardData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dashboardData = action.payload;
        state.lastUpdated = new Date().toISOString();
        state.error = null;
      })
      .addCase(refreshDashboardData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  clearDashboardData,
  updateStats,
  addRecentActivity,
  removeRecentActivity,
} = customerDashboardSlice.actions;

export default customerDashboardSlice.reducer;
